import {ApolloProvider, getApolloContext} from '@apollo/client';
import {ThemeProvider} from '@benchling/nucleus-ui';
import React, {ReactElement, useMemo} from 'react';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import {Provider} from 'react-redux';
import {Store} from 'redux';

import {getApolloClient} from 'graphql-client/ApolloClient';
import LaunchDarklyProvider from 'launchdarkly/LaunchDarklyProvider';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';

export const stubbableFunctions = {getApolloClient};

export function renderToDOM(element: ReactElement<unknown>, node: Element, callback?: () => void): void {
  ReactDOM.render(appProviderWrapper(element), node, callback);
}

export function appProviderWrapper(element: ReactElement<unknown>): JSX.Element {
  return (
    <LaunchDarklyProvider>
      <ThemeProvider>
        <MemoizedApolloProvider client={stubbableFunctions.getApolloClient()}>
          <Provider store={getStore() as Store<BenchlingState>}>{element}</Provider>
        </MemoizedApolloProvider>
      </ThemeProvider>
    </LaunchDarklyProvider>
  );
}

export function renderToStaticMarkup(element: JSX.Element): string {
  return ReactDOMServer.renderToStaticMarkup(
    <LaunchDarklyProvider>
      <ThemeProvider>
        <ApolloProvider client={stubbableFunctions.getApolloClient()}>
          <Provider store={getStore() as Store<BenchlingState>}>{element}</Provider>
        </ApolloProvider>
      </ThemeProvider>
    </LaunchDarklyProvider>
  );
}

/**
 * A replacement for ApolloProvider that memoizes the ApolloContext value based on shallow equality of the
 * context's client.
 *
 * This avoids an issue where ApolloProvider re-renders its consumers whenever the provider is re-rendered,
 * rather than only when the client has changed. (https://github.com/apollographql/react-apollo/issues/3595)
 *
 * The ApolloContext is consumed by hooks such as useQuery, which would fire whenever the ApolloProvider's
 * parent re-renders. Any component that uses this hook would re-render when its ancestor renders the
 * provider, even if its immediate parent _doesn't_ re-render.
 *
 * This caused a noticeable slowdown in large notebook entries, because each chip in a table uses its own
 * Apollo query.
 */
const MemoizedApolloProvider: typeof ApolloProvider = ({client, children}) => {
  const ApolloContext = getApolloContext();
  const value = useMemo(() => ({client}), [client]);
  return <ApolloContext.Provider value={value}>{children}</ApolloContext.Provider>;
};
